import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExactConnectionState } from '../../api/exactConnection';
import { formatDuration } from '../../api/queueMonitor';
import { ExactConnectionMonitor } from '../../hooks/useExactConnection';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { ExactRateLimitBar } from './ExactRateLimitBar';
import { LiveExactLimits } from '../Rollout/LiveExactLimits';
import { ExactTokenTimeline } from './ExactTokenTimeline';
import './ExactConnection.scss';

export interface IExactConnectionPanelProps {
    monitor: ExactConnectionMonitor;
    open: boolean;
    onToggle: () => void;
}

const STATE_LABEL: Record<ExactConnectionState, string> = {
    Unknown: 'Onbekend',
    NotConfigured: 'Niet ingesteld',
    NoToken: 'Geen token',
    Connected: 'Verbonden',
    Refreshing: 'Wordt vernieuwd',
    Expired: 'Verlopen',
};

/**
 * How often the countdown is redrawn.
 *
 * A token lives ten minutes, so "nog 4 min" written once is wrong within the minute. One
 * second is cheap here: this panel is a header and a handful of lines, and nothing else on
 * the page re-renders with it.
 */
const TICK_MS = 1000;

/** What the connector does about the token on its own, so nobody refreshes for nothing. */
const REFRESH_HINT =
    'De connector kijkt elke minuut en ververst het access token zelf vlak voor het verloopt, met het refresh token dat er al ligt. Handmatig verversen is dus alleen nodig als de stand hierboven zegt dat het is verlopen en er niet meer vernieuwd wordt.';

const VALID_UNTIL_HINT =
    'Tot dit moment neemt Exact het huidige access token aan. Exact geeft ze voor tien minuten af, dus dit staat er kort op: dat het aftikt is geen storing.';

/**
 * What is going on, in plain language.
 *
 * The API ships a summary along with this, but that one is written for Swagger and log
 * lines. On screen the text should be the one telling you what to do next.
 */
const explain = (
    state: ExactConnectionState,
    expiresInSeconds?: number | null
): string => {
    switch (state) {
        case 'Connected':
            return `Alles in orde. Het access token is nog ${
                formatDuration(expiresInSeconds) ?? 'even'
            } geldig.`;

        case 'Refreshing':
            return 'Het access token is net verlopen: verversen lukte niet, bijvoorbeeld omdat Exact even onbereikbaar was. De timer probeert het elke minuut opnieuw; tot dan faalt alles naar Exact met 401.';

        case 'Expired':
            return `Verlopen ${
                formatDuration(expiresInSeconds ? -expiresInSeconds : null) ??
                'een tijd'
            } geleden, en er wordt niet meer vernieuwd. Probeer eerst verversen; lukt dat niet, dan neemt Exact het refresh token niet meer aan en is opnieuw inloggen nodig.`;

        case 'NoToken':
            return 'Deze omgeving heeft nog nooit een token opgeslagen. Verbind opnieuw via de browser.';

        case 'NotConfigured':
            return 'Er is nog geen client id, client secret of terugkeeradres ingesteld voor Exact. Zonder die drie valt er niets te verbinden.';

        default:
            return 'De stand van de Exact-verbinding kon niet worden bepaald.';
    }
};

/** A timestamp in readable Dutch form. */
const formatMoment = (iso?: string | null): string | null => {
    if (!iso) {
        return null;
    }

    const parsed = Date.parse(iso);

    if (Number.isNaN(parsed)) {
        return null;
    }

    return new Date(parsed).toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * The same timestamp without the parts the reader already knows.
 *
 * A token that runs out in seven minutes runs out today, and "2 sep 2026 14:18" spends four
 * of its five words saying so. The date only appears once it is not today after all - which
 * is the case that is worth noticing.
 */
const formatClock = (
    iso?: string | null,
    now: number = Date.now()
): string | null => {
    if (!iso) {
        return null;
    }

    const parsed = Date.parse(iso);

    if (Number.isNaN(parsed)) {
        return null;
    }

    const moment = new Date(parsed);
    const time = moment.toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
    });

    return moment.toDateString() === new Date(now).toDateString()
        ? time
        : `${moment.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short',
          })} ${time}`;
};

/**
 * The connection to Exact, with the two buttons that repair it.
 *
 * Refreshing goes through the refresh token that is already there and costs nobody
 * anything, so that is the first button to try. Once Exact stops accepting that token a
 * human has to sign in - there is no way around that. What this button does take away is
 * the manual work around it: the URL is assembled here from the client id and the return
 * address of this environment, and as soon as the tokens are updated this screen notices
 * by itself.
 */
export const ExactConnectionPanel = ({
    monitor,
    open,
    onToggle,
}: IExactConnectionPanelProps) => {
    const [copied, setCopied] = useState<boolean>(false);

    const connection = monitor.connection;
    const state = connection?.state ?? 'Unknown';
    const canReconnect = !!connection?.authorizeUrl;

    const expiresAt = connection?.expiresAt;

    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        if (!expiresAt) {
            return;
        }

        const timer = setInterval(() => setNow(Date.now()), TICK_MS);

        return () => clearInterval(timer);
    }, [expiresAt]);

    /*
        Counted off the moment itself rather than taken from the API.

        `expiresInSeconds` was true when the call was answered and is a minute stale by the
        time somebody reads it, while the moment it was derived from stays true. Where the
        API sent no moment at all, its own number is still the best there is.
    */
    const secondsLeft = useMemo(() => {
        if (!expiresAt) {
            return connection?.expiresInSeconds ?? null;
        }

        const parsed = Date.parse(expiresAt);

        return Number.isNaN(parsed)
            ? connection?.expiresInSeconds ?? null
            : (parsed - now) / 1000;
    }, [expiresAt, now, connection?.expiresInSeconds]);

    /** The countdown as a phrase, on both sides of zero. */
    const validityLabel = useMemo(() => {
        if (secondsLeft === null || secondsLeft === undefined) {
            return null;
        }

        return secondsLeft >= 0
            ? `nog ${formatDuration(secondsLeft) ?? 'even'}`
            : `verlopen, ${formatDuration(-secondsLeft) ?? 'net'} geleden`;
    }, [secondsLeft]);

    const copy = () => {
        if (!connection?.authorizeUrl) {
            return;
        }

        navigator.clipboard
            ?.writeText(connection.authorizeUrl)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
    };

    const reconnectedAt = formatMoment(connection?.reconnectedAt);
    const validUntil = formatClock(expiresAt, now);

    return (
        <CollapsibleSection
            id="exact-connection"
            className="exact-connection"
            title="Exact-verbinding"
            open={open}
            onToggle={onToggle}
            status={
                <>
                    <span
                        className={`exact-connection__state exact-connection__state--${state.toLowerCase()}`}
                    >
                        <span className="exact-connection__dot" />
                        {STATE_LABEL[state]}
                    </span>

                    {/*
                        In the header rather than only in the body, because this is the one
                        thing worth knowing about the connection without opening it: whether
                        the token is about to run out.
                    */}
                    {validUntil && validityLabel && (
                        <span
                            className="exact-connection__countdown"
                            title={VALID_UNTIL_HINT}
                        >
                            token geldig tot {validUntil} ({validityLabel})
                        </span>
                    )}

                    {monitor.loading && (
                        <span className="exact-connection__loading">
                            <FontAwesomeIcon icon="spinner" spin /> ophalen...
                        </span>
                    )}
                </>
            }
            tools={
                <button
                    type="button"
                    className="exact-connection__reload"
                    onClick={monitor.reload}
                    title="Nu opnieuw kijken"
                >
                    <FontAwesomeIcon icon="rotate" />
                    <span className="visually-hidden">Nu opnieuw kijken</span>
                </button>
            }
        >
            {connection && (
                <>
                    <p className="exact-connection__explain">
                        {explain(state, secondsLeft)}
                    </p>

                    <dl className="exact-connection__facts">
                        {/*
                            First, because it is the question this panel gets asked: until
                            when does the current token work, and when would I have to do
                            something about it.
                        */}
                        <div>
                            <dt title={VALID_UNTIL_HINT}>Token geldig tot</dt>
                            <dd>
                                {validUntil
                                    ? `${validUntil} · ${validityLabel}`
                                    : 'onbekend'}
                            </dd>
                        </div>

                        <div>
                            <dt title={REFRESH_HINT}>Automatisch verversen</dt>
                            <dd>
                                {connection.hasRefreshToken
                                    ? 'door de connector zelf, vlak voor het verloopt'
                                    : 'nee, er ligt geen refresh token'}
                            </dd>
                        </div>

                        {connection.division !== null &&
                            connection.division !== undefined && (
                                <div>
                                    <dt>Divisie</dt>
                                    <dd>{connection.division}</dd>
                                </div>
                            )}

                        <div>
                            <dt>Omgeving bij Exact</dt>
                            <dd>{connection.endpoint}</dd>
                        </div>

                        <div>
                            <dt>Laatst handmatig verbonden</dt>
                            <dd>{reconnectedAt ?? 'onbekend'}</dd>
                        </div>

                        <div>
                            <dt>Refresh token aanwezig</dt>
                            <dd>{connection.hasRefreshToken ? 'ja' : 'nee'}</dd>
                        </div>
                    </dl>

                    {/*
                        The other thing Exact can refuse over, and the one this panel used to
                        be silent about. A token that is fine and a budget that is gone look
                        the same from the outside: everything to Exact fails at once.
                    */}
                    <ExactRateLimitBar rateLimit={monitor.rateLimit} />

                    {/* Day and minute as the connector's last call saw them, pushed live. */}
                    {connection.customer && connection.environment && (
                        <LiveExactLimits customer={connection.customer} environment={connection.environment} />
                    )}

                    {open && connection.customer && connection.environment && (
                        <ExactTokenTimeline customer={connection.customer} environment={connection.environment} />
                    )}

                    {connection.redirectUriWarning && (
                        <p className="exact-connection__warning" role="alert">
                            <FontAwesomeIcon icon="triangle-exclamation" />{' '}
                            {connection.redirectUriWarning}
                        </p>
                    )}

                    {monitor.error && (
                        <p className="exact-connection__error" role="alert">
                            {monitor.error}
                        </p>
                    )}

                    {/* Refreshing does nothing to a token that is still valid, and quietly
                        calling that "done" would make the button untrustworthy. */}
                    {connection.refreshed !== null &&
                        connection.refreshed !== undefined &&
                        !monitor.error && (
                            <p className="exact-connection__note">
                                <FontAwesomeIcon icon="check" />{' '}
                                {connection.refreshed
                                    ? 'Token vernieuwd.'
                                    : 'Het token was nog geldig, dus er hoefde niets vernieuwd te worden.'}
                            </p>
                        )}

                    <div className="exact-connection__actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={
                                monitor.refreshing ||
                                !connection.hasRefreshToken ||
                                state === 'NotConfigured'
                            }
                            onClick={monitor.refresh}
                            title={REFRESH_HINT}
                        >
                            {monitor.refreshing && (
                                <FontAwesomeIcon
                                    icon="spinner"
                                    spin
                                    className="me-2"
                                />
                            )}
                            Token verversen
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={!canReconnect || monitor.awaitingLogin}
                            onClick={monitor.reconnect}
                            title={
                                canReconnect
                                    ? 'Opent de Exact-login in een nieuw tabblad. Na het inloggen worden de tokens bijgewerkt en ziet dit scherm dat zelf.'
                                    : 'Er is geen client id of terugkeeradres ingesteld, dus er valt niets te openen.'
                            }
                        >
                            <FontAwesomeIcon
                                icon="arrow-up-right-from-square"
                                className="me-2"
                            />
                            Opnieuw verbinden met Exact
                        </button>
                    </div>

                    {monitor.awaitingLogin && (
                        <div className="exact-connection__waiting">
                            <p>
                                <FontAwesomeIcon icon="spinner" spin /> Log in
                                bij Exact in het tabblad dat net is geopend.
                                Zodra de tokens zijn bijgewerkt, ziet dit scherm
                                dat zelf.
                            </p>

                            <div className="exact-connection__waiting-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={monitor.reload}
                                >
                                    Nu controleren
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-link btn-sm"
                                    onClick={monitor.stopWaiting}
                                >
                                    Stoppen met wachten
                                </button>
                            </div>
                        </div>
                    )}

                    {canReconnect && (
                        <details className="exact-connection__url">
                            <summary>
                                De URL die geopend wordt, voor het geval je
                                browser het tabblad blokkeert
                            </summary>

                            <code>{connection.authorizeUrl}</code>

                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={copy}
                            >
                                {copied ? (
                                    <>
                                        <FontAwesomeIcon
                                            icon="check"
                                            className="me-2"
                                        />
                                        Gekopieerd
                                    </>
                                ) : (
                                    'Kopieer'
                                )}
                            </button>
                        </details>
                    )}
                </>
            )}

            {!connection && !monitor.loading && (
                <p className="exact-connection__explain">
                    {monitor.error ??
                        'De Exact-verbinding kon niet worden opgehaald.'}
                </p>
            )}
        </CollapsibleSection>
    );
};
